// ── <TrackRecord> — "How often Sayer is right" ──
//
// The calibration chart the website promises: forecasts grouped by the probability Sayer gave
// them (x) against how often those events actually happened (y). Fed by GET /api/calibration
// (server/lib/calibration.js): {as_of, models: {<id>: {bins: [{lo, hi, n, accuracy}], total_n,
// avg_brier, ece}}}. With no resolved forecasts to show, it says so and draws an example chart
// that is labelled as one.
import React, { useMemo, useState } from "react";
import { C } from "../lib/colors.js";
import { ModalShell } from "./Panels.jsx";
import SegmentedControl from "./SegmentedControl.jsx";
import { IconButton } from "./ui/Primitives.jsx";
import { ui } from "../lib/telemetry/instrument.js";
import { IconClose, IconInfo } from "./ui/Icons.jsx";
import { product as brandProduct } from "../branding.js";

// Plausible, clearly-fake bins for the empty state. Never shown without the "Example data" label.
const EXAMPLE_BINS = [
  { lo: 0, hi: 0.1, n: 38, accuracy: 0.05 },
  { lo: 0.1, hi: 0.2, n: 52, accuracy: 0.13 },
  { lo: 0.2, hi: 0.3, n: 61, accuracy: 0.27 },
  { lo: 0.3, hi: 0.4, n: 47, accuracy: 0.32 },
  { lo: 0.4, hi: 0.5, n: 40, accuracy: 0.48 },
  { lo: 0.5, hi: 0.6, n: 43, accuracy: 0.56 },
  { lo: 0.6, hi: 0.7, n: 49, accuracy: 0.64 },
  { lo: 0.7, hi: 0.8, n: 44, accuracy: 0.71 },
  { lo: 0.8, hi: 0.9, n: 35, accuracy: 0.87 },
  { lo: 0.9, hi: 1, n: 27, accuracy: 0.93 },
];

/**
 * The calibration series to show: the product's own model when the snapshot has it, else the
 * one with the most resolved forecasts. Null when nothing has resolved.
 */
function pickModel(calibration, modelId, strict = false) {
  const models = calibration?.models;
  if (!models || typeof models !== "object") return null;
  const usable = (m) =>
    m &&
    Array.isArray(m.bins) &&
    m.bins.some((b) => b && b.n > 0 && typeof b.accuracy === "number");
  if (usable(models[modelId])) return models[modelId];
  if (strict) return null;
  let best = null;
  for (const m of Object.values(models)) {
    if (!usable(m)) continue;
    const n = m.total_n ?? m.bins.reduce((s, b) => s + (b.n || 0), 0);
    if (!best || n > best.n) best = { m, n };
  }
  return best ? best.m : null;
}

// Below this many resolved forecasts in a probability band, its point is drawn faint; below
// THIN_SAMPLE in total, the page says the chart is still noisy.
const MIN_BIN_N = 10;
const THIN_SAMPLE = 300;
const pct = (v) => `${Math.round(v * 100)}%`;
const range = (b) => `${Math.round(b.lo * 100)}–${Math.round(b.hi * 100)}%`;

// Chart geometry (SVG user units). The plot is square so the diagonal sits at 45°.
const W = 480;
const PAD_L = 52;
const PAD_T = 14;
const PLOT = 410;
const PAD_B = 52;
const H = PAD_T + PLOT + PAD_B;
const TICKS = [0, 0.2, 0.4, 0.6, 0.8, 1];

/** The calibration chart: predicted probability (x) against observed frequency (y). */
function CalibrationChart({ bins, example }) {
  const [hover, setHover] = useState(null);
  const pts = bins.filter((b) => b.n > 0 && typeof b.accuracy === "number");
  // Perfect calibration is two lines half a bin above and below the diagonal: a point sits on
  // one when its outcome rate is its bin's edge (a 70–80% bin's point, drawn at 75%, at 70 or 80).
  const spread = (bins[0].hi - bins[0].lo) / 2;
  const maxN = Math.max(1, ...pts.map((b) => b.n));
  const x = (v) => PAD_L + v * PLOT;
  const y = (v) => PAD_T + (1 - v) * PLOT;
  const radius = (n) => 5 + 9 * Math.sqrt(n / maxN);
  const markFill = example ? C.gray : C.accent;
  const hp = hover != null ? pts[hover] : null;
  return (
    <div>
      {/* Key: what the line and the points are; the example badge sits with it. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          paddingLeft: `${(PAD_L / W) * 100}%`,
          marginBottom: 10,
          fontSize: 12,
          color: C.grayL,
          flexWrap: "wrap",
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <svg width="16" height="16" aria-hidden="true">
            {[-1.5, 1.5].map((d) => (
              <line
                key={d}
                x1={3 + d}
                y1={13 + d}
                x2={13 + d}
                y2={3 + d}
                style={{ stroke: C.grayL, strokeWidth: 1.25, opacity: 0.7 }}
              />
            ))}
          </svg>
          Perfect calibration
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <svg width="16" height="16" aria-hidden="true">
            <circle
              cx="8"
              cy="8"
              r="5"
              style={{ fill: markFill, fillOpacity: example ? 0.55 : 0.9 }}
            />
          </svg>
          Resolved forecasts
        </span>
      </div>
      <div style={{ position: "relative" }}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          role="img"
          aria-label={
            example
              ? "Example calibration chart (illustrative data)"
              : "Calibration chart: forecast probability against how often the event happened"
          }
          style={{ display: "block", overflow: "visible" }}
        >
          {/* Regions, named in plain words so the chart reads without a legend. */}
          <text x={x(0.03)} y={y(0.93)} style={{ fill: C.gray, fontSize: 12 }}>
            Happened more often
          </text>
          <text x={x(0.97)} y={y(0.05)} textAnchor="end" style={{ fill: C.gray, fontSize: 12 }}>
            Happened less often
          </text>
          {/* Hairline grid. */}
          {TICKS.map((t) => (
            <g key={t}>
              <line
                x1={x(t)}
                x2={x(t)}
                y1={y(0)}
                y2={y(1)}
                style={{ stroke: C.border, strokeWidth: 1 }}
              />
              <line
                x1={x(0)}
                x2={x(1)}
                y1={y(t)}
                y2={y(t)}
                style={{ stroke: C.border, strokeWidth: 1 }}
              />
              <text
                x={x(t)}
                y={y(0) + 18}
                textAnchor="middle"
                className="sy-num"
                style={{ fill: C.gray, fontSize: 11 }}
              >
                {pct(t)}
              </text>
              <text
                x={x(0) - 8}
                y={y(t) + 4}
                textAnchor="end"
                className="sy-num"
                style={{ fill: C.gray, fontSize: 11 }}
              >
                {pct(t)}
              </text>
            </g>
          ))}
          {/* Axis titles. */}
          <text
            x={x(0.5)}
            y={y(0) + 42}
            textAnchor="middle"
            style={{ fill: C.grayL, fontSize: 12, fontWeight: 500 }}
          >
            {brandProduct.prose}&apos;s forecast
          </text>
          <text
            transform={`translate(${x(0) - 40} ${y(0.5)}) rotate(-90)`}
            textAnchor="middle"
            style={{ fill: C.grayL, fontSize: 12, fontWeight: 500 }}
          >
            How often it happened
          </text>
          {/* Perfect calibration: the two lines, clipped to the plot. */}
          {[
            [0, spread, 1 - spread, 1],
            [spread, 0, 1, 1 - spread],
          ].map(([u1, v1, u2, v2]) => (
            <line
              key={`${u1}-${v1}`}
              data-calibration-line=""
              x1={x(u1)}
              y1={y(v1)}
              x2={x(u2)}
              y2={y(v2)}
              style={{ stroke: C.grayL, strokeWidth: 1.5, opacity: 0.55 }}
            />
          ))}
          {/* The miss: a stem from the diagonal to each point. */}
          {pts.map((b, i) => {
            const mid = (b.lo + b.hi) / 2;
            return (
              <line
                key={`s${i}`}
                x1={x(mid)}
                x2={x(mid)}
                y1={y(mid)}
                y2={y(b.accuracy)}
                style={{ stroke: markFill, strokeWidth: 2, opacity: 0.35, strokeLinecap: "round" }}
              />
            );
          })}
          {pts.map((b, i) => {
            const mid = (b.lo + b.hi) / 2;
            const r = radius(b.n);
            const on = hover === i;
            return (
              <g
                key={`p${i}`}
                {...ui("track-record.chart-point", i)}
                onPointerEnter={() => setHover(i)}
                onPointerLeave={() => setHover((h) => (h === i ? null : h))}
                onFocus={() => setHover(i)}
                onBlur={() => setHover((h) => (h === i ? null : h))}
                tabIndex={0}
                role="img"
                aria-label={`Forecasts at ${range(b)}: ${b.n} resolved, ${pct(b.accuracy)} happened`}
                style={{ cursor: "default", outline: "none" }}
              >
                <circle cx={x(mid)} cy={y(b.accuracy)} r={r + 8} style={{ fill: "transparent" }} />
                <circle
                  cx={x(mid)}
                  cy={y(b.accuracy)}
                  r={on ? r + 2 : r}
                  style={{
                    fill: markFill,
                    fillOpacity: example ? 0.55 : b.n < MIN_BIN_N ? 0.3 : 0.9,
                    transition: "r .12s ease",
                  }}
                />
              </g>
            );
          })}
        </svg>
        {/* The example's label sits in the empty upper-left of the plot, where no example point
          falls, so it reads as part of the chart rather than a caption beside it. */}
        {example && (
          <span
            style={{
              position: "absolute",
              left: `${((PAD_L + 0.04 * PLOT) / W) * 100}%`,
              top: `${((PAD_T + 0.13 * PLOT) / H) * 100}%`,
              fontSize: 12,
              fontWeight: 500,
              color: C.white,
              background: C.rowActive,
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              padding: "3px 8px",
              pointerEvents: "none",
            }}
          >
            Example data
          </span>
        )}
        {hp && (
          <div
            role="status"
            style={{
              position: "absolute",
              left: `${(((x((hp.lo + hp.hi) / 2) + 14) / W) * 100).toFixed(2)}%`,
              top: `${(((y(hp.accuracy) - 18) / H) * 100).toFixed(2)}%`,
              transform: (hp.lo + hp.hi) / 2 > 0.6 ? "translateX(calc(-100% - 28px))" : undefined,
              pointerEvents: "none",
              background: C.tooltipBg,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              boxShadow: C.shadowBox,
              padding: "8px 10px",
              fontSize: 12,
              color: C.white,
              whiteSpace: "nowrap",
              lineHeight: 1.5,
            }}
          >
            <div style={{ color: C.gray }}>Forecasts at {range(hp)}</div>
            <div>
              <span className="sy-num">{hp.n}</span> resolved ·{" "}
              <span className="sy-num">{pct(hp.accuracy)}</span> happened
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** One plain stat: a label, the figure, and a line saying how to read it. */
function Stat({ label, value, note }) {
  return (
    <div style={{ padding: "12px 0", borderTop: `1px solid ${C.border}` }}>
      <div style={{ fontSize: 12, color: C.gray }}>{label}</div>
      <div
        style={{
          fontSize: 24,
          fontWeight: 600,
          color: C.white,
          lineHeight: 1.2,
          marginTop: 2,
        }}
      >
        {value}
      </div>
      {note ? (
        <div style={{ fontSize: 12, color: C.gray, marginTop: 4, lineHeight: 1.45 }}>{note}</div>
      ) : null}
    </div>
  );
}

export default function TrackRecord({
  calibration,
  modelId,
  tiers = null,
  initialTier = "quick",
  onClose,
}) {
  // Each depth's record is its CURRENT model's (tiers[].modelId); without tiers, the fixed model.
  const [tierKey, setTierKey] = useState(
    tiers?.some((t) => t.key === initialTier) ? initialTier : tiers?.[0]?.key,
  );
  const tier = tiers?.find((t) => t.key === tierKey) || null;
  const model = useMemo(
    () => (tier ? pickModel(calibration, tier.modelId, true) : pickModel(calibration, modelId)),
    [calibration, modelId, tier],
  );
  const example = !model;
  const bins = model ? model.bins : EXAMPLE_BINS;
  const resolved = model ? (model.total_n ?? model.bins.reduce((s, b) => s + (b.n || 0), 0)) : 0;
  const asOf = calibration?.as_of ? new Date(calibration.as_of) : null;
  const asOfText =
    asOf && !Number.isNaN(asOf.getTime())
      ? asOf.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
      : null;
  return (
    <ModalShell
      onClose={onClose}
      zIndex={550}
      surface="track-record"
      panelProps={{ "data-track-record-panel": "" }}
      panelStyle={{
        width: "min(860px, 94vw)",
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        boxShadow: C.shadowBox,
        marginTop: "4vh",
      }}
    >
      <div style={{ padding: "24px 28px 0", position: "relative" }}>
        <h2
          style={{
            margin: 0,
            fontSize: 24,
            fontWeight: 600,
            color: C.white,
            letterSpacing: 0,
            lineHeight: 1.25,
          }}
        >
          How often {brandProduct.prose} is right
        </h2>
        <p
          style={{
            margin: "8px 0 0",
            maxWidth: 620,
            fontSize: 15,
            lineHeight: 1.5,
            color: C.grayL,
          }}
        >
          When {brandProduct.prose} says something has a 70% chance, it should happen about 7 times
          in 10. This chart checks that against every forecast that has resolved.
        </p>
        {tiers && tiers.length > 1 && (
          <div style={{ marginTop: 16 }}>
            <SegmentedControl
              label="Depth"
              options={tiers.map((t) => ({
                value: t.key,
                label: t.label,
                ui: ui("track-record.depth", t.key),
              }))}
              value={tierKey}
              onChange={setTierKey}
            />
          </div>
        )}
        <div style={{ position: "absolute", top: 16, right: 16 }}>
          <IconButton
            label="Close"
            {...ui("track-record.close")}
            onClick={onClose}
            style={{ padding: 0 }}
          >
            <IconClose />
          </IconButton>
        </div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 440px) minmax(240px, 1fr)",
          gap: 32,
          padding: "20px 28px 28px",
          alignItems: "start",
        }}
      >
        <div style={{ minWidth: 0, maxWidth: 440 }}>
          <CalibrationChart bins={bins} example={example} />
          {example && (
            <div style={{ fontSize: 12, color: C.gray, marginTop: 8 }}>
              Illustrative data, not {brandProduct.prose}&apos;s results.
            </div>
          )}
        </div>
        <div>
          {example ? (
            <div
              style={{
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
                padding: "12px 14px",
                background: C.rowInactive,
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                marginBottom: 16,
              }}
            >
              <IconInfo
                width={16}
                height={16}
                style={{ color: C.accent, flexShrink: 0, marginTop: 2 }}
              />
              <div style={{ fontSize: 13, lineHeight: 1.5, color: C.grayL }}>
                <div style={{ color: C.white, fontWeight: 500, marginBottom: 2 }}>
                  {tier ? `No resolved ${tier.label} forecasts yet` : "No resolved forecasts yet"}
                </div>
                The chart fills in as forecasts resolve. Until then, this example shows how it
                reads.
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: 16 }}>
              <Stat
                label="Resolved forecasts"
                value={<span className="sy-num">{resolved.toLocaleString()}</span>}
                note={
                  resolved < THIN_SAMPLE
                    ? `Still a small sample, so the chart is noisy. Faint points have fewer than ${MIN_BIN_N} forecasts.`
                    : undefined
                }
              />
              {typeof model.avg_brier === "number" && (
                <Stat
                  label="Brier score"
                  value={<span className="sy-num">{model.avg_brier.toFixed(3)}</span>}
                  note="Lower is better. 0 is perfect; always saying 50% scores 0.250."
                />
              )}
            </div>
          )}
          <div style={{ fontSize: 13, fontWeight: 500, color: C.white, marginBottom: 8 }}>
            How to read it
          </div>
          <ul
            style={{
              margin: 0,
              paddingLeft: 18,
              fontSize: 13,
              lineHeight: 1.55,
              color: C.grayL,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <li>Each point groups forecasts made at a similar probability.</li>
            <li>
              Between the two lines, the numbers held up: forecasts of 70–80% happened 70–80% of the
              time.
            </li>
            <li>
              Above the upper line, events happened more often than forecast; below the lower line,
              less often.
            </li>
            <li>Bigger points stand for more resolved forecasts.</li>
          </ul>
          {asOfText && (
            <div style={{ fontSize: 12, color: C.gray, marginTop: 16 }}>Updated {asOfText}</div>
          )}
        </div>
      </div>
    </ModalShell>
  );
}
